import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';

import { Box, Button, Divider, Popover, Stack, TextField, Typography } from '@mui/material';
import { popoverPaperStyles } from '@pages/CustomPoiTypes.styles';
import { translatedCount, type LocalizedText } from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import { GlobeIcon } from 'react-line-awesome';

interface Props {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  autoFocus?: boolean;
  size?: 'small' | 'medium';
}

/**
 * An input for one user-authored label across every enabled locale.
 *
 * The box itself edits the primary locale — the one the server requires, so the common
 * case stays a single field — and the badge opens the rest. The badge doubles as the
 * translation status, which is the only place the UI shows that a label is incomplete;
 * an untranslated locale is legal and falls back at render time.
 */
const LocalizedTextField: React.FC<Props> = ({
  label, value, onChange, placeholder, helperText, required, autoFocus, size = 'small',
}) => {
  const { t } = useLocalization();
  const { primary, enabled } = useCustomizationLocales();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const filled = translatedCount(value, enabled);
  const isComplete = filled === enabled.length;

  const setLocale = (locale: string, text: string) => onChange({ ...value, [locale]: text });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          size={size}
          label={label}
          required={required}
          autoFocus={autoFocus}
          placeholder={placeholder}
          helperText={helperText}
          value={value[primary] ?? ''}
          onChange={(event) => setLocale(primary, event.target.value)}
        />
        {/* Only worth showing when there is more than one language to fill in. */}
        {enabled.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            color={isComplete ? 'inherit' : 'warning'}
            startIcon={<GlobeIcon />}
            onClick={(event) => setAnchor(event.currentTarget)}
            // Matched to the input's own height rather than nudged with a margin, so the two
            // stay level whatever the field size is. `flex-start` keeps them aligned when a
            // helper text makes the field taller than the button.
            sx={{ height: size === 'small' ? 40 : 56, flex: 'none', minWidth: 0 }}
            title={t('Customization_EditTranslations')}
          >
            {filled}/{enabled.length}
          </Button>
        )}
      </Stack>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        {/* Header, divider, body — the same three-part chrome the drawers use. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ color: 'text.secondary', display: 'inline-flex', fontSize: 15 }}><GlobeIcon /></Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('Customization_Translations')}
          </Typography>
        </Stack>

        <Divider />

        <Stack spacing={1.5} sx={{ p: 2 }}>
          {enabled.map((locale) => (
            <TextField
              key={locale}
              fullWidth
              size="small"
              label={locale === primary
                ? `${locale.toUpperCase()} · ${t('Customization_PrimaryLocale')}`
                : locale.toUpperCase()}
              value={value[locale] ?? ''}
              onChange={(event) => setLocale(locale, event.target.value)}
              placeholder={locale === primary ? undefined : t('Customization_FallsBackToPrimary')}
            />
          ))}

          <Typography variant="caption" color="text.secondary">
            {t('Customization_EmptyLocalesFallBack')}
          </Typography>
        </Stack>
      </Popover>
    </Box>
  );
};

export default LocalizedTextField;
